/**
 * V1 Page Renderer
 * 
 * Renders a page of questions for V1 forms and surveys.
 */

import React from 'react';
import type { V1Page, V1QuestionResponse } from './types/v1-questionnaire.types';
import { V1QuestionRenderer } from './V1QuestionRenderer';

interface V1PageRendererProps {
  page: V1Page;
  responses: Record<string, V1QuestionResponse>;
  validationErrors: Record<string, string>;
  touchedQuestions: Set<string>;
  onChange: (questionId: string, response: V1QuestionResponse) => void;
  onBlur?: (questionId: string) => void;
  primaryColor?: string;
}

export const V1PageRenderer: React.FC<V1PageRendererProps> = ({
  page,
  responses,
  validationErrors,
  touchedQuestions,
  onChange,
  onBlur,
  primaryColor,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Page title and description */}
      {page.title && (
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {page.title}
        </h2>
      )}
      
      {page.description && (
        <p className="text-gray-600 mb-6">{page.description}</p>
      )}
      
      {/* Render questions */}
      <div>
        {page.questions
          .sort((a, b) => a.position - b.position)
          .map((question) => (
            <V1QuestionRenderer
              key={question.id}
              question={question}
              response={responses[question.id] || { questionId: question.id }}
              onChange={(response) => onChange(question.id, response)}
              onBlur={() => onBlur?.(question.id)}
              validationError={validationErrors[question.id]}
              showError={touchedQuestions.has(question.id)}
              primaryColor={primaryColor}
            />
          ))}
      </div>
    </div>
  );
};